import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, Mic, FileCheck, History as HistoryIcon, ListTodo, CheckCircle2, Users, Clock, CalendarClock, AlertTriangle, Paperclip, MapPin } from "lucide-react";
import api from "@/lib/api";
import { localeUI, TL, jazykUI } from "@/lib/prekladyUI";
import { menaTenanta } from "@/lib/tenantMena";
import { sJednotkou } from "@/lib/castka";
import { CT_LABEL } from "@/lib/servisRadar";

// Robustnost: každý zdroj může přijít jako ne-pole nebo s null-položkami.
// asArr → čisté pole objektů, ať .map/.filter na null-položce nespadne (bílá stránka).
const asArr = (v) => (Array.isArray(v) ? v.filter((x) => x && typeof x === "object") : []);

/**
 * HistoryTimeline (v4.6 — Premium Warm Depth + „Co nás čeká / Co máme za sebou")
 *
 * Aggregates 4 sources for a single client:
 *   • /api/activities?contact_id=…       (manual / system)
 *   • /api/voice-notes (filtered)        (each voice note = one event)
 *   • /api/contracts?contact_id=…        (contract creation)
 *   • /api/crm/tasks?contact_id=…        (Diář events + lifecycle tasks)
 *
 * Then splits chronologicky:
 *   ▸ FUTURE   = "Co nás čeká"     (gold accent, sort ASC by date)
 *   ▸ PAST     = "Co máme za sebou" (sage accent, sort DESC by date)
 *
 * Designed to feel luxurious — animated entrance, soft cognac timeline rail,
 * countdown badges (např. „za 6 dní" / „před 1716 dny"), avatar of who did it.
 */

/* Jména zdrojů v POŘADÍ, v jakém se stahují v `Promise.all` níž.
 * Slouží jen k tomu, aby se dalo napsat, CO se nenačetlo — „něco se nenačetlo"
 * je pro poradce k ničemu. Když přidáš zdroj, přidej ho na obě místa. */
export const ZDROJE = ["Aktivity", "Hlasovky", "Smlouvy", "Úkoly a schůzky"];

/** Věta o nenačtených zdrojích. Prázdný seznam → `null` (nic se nevykreslí). */
export function hlasenkaZdroju(chybne) {
  const list = Array.isArray(chybne) ? chybne.filter(Boolean) : [];
  if (!list.length) return null;
  return TL("Tohle se nenačetlo: {co}. Historie níž proto NENÍ úplná.").replace("{co}", list.map((z) => TL(z)).join(", "));
}

const FILTERS = [
  { id: "all",     label: "Vše",       icon: HistoryIcon },
  { id: "meeting", label: "Schůzky",   icon: Users },
  { id: "voice",   label: "Hlasovky",  icon: Mic },
  { id: "call",    label: "Telefon",   icon: Phone },
  { id: "email",   label: "E-mail",    icon: Mail },
  { id: "contract",label: "Smlouvy",   icon: FileCheck },
  { id: "task",    label: "Úkoly",     icon: ListTodo },
];

const TYPE_META = {
  /* Typy aktivit = tokeny diáře --akt-* (CRM-09), dřív iOS paleta natvrdo. */
  voice:    { icon: Mic,          color: "var(--akt-dopis)",     label: "Hlasovka" },
  call:     { icon: Phone,        color: "var(--akt-telefonat)", label: "Telefon" },
  email:    { icon: Mail,         color: "var(--akt-email)",     label: "E-mail" },
  meeting:  { icon: Users,        color: "var(--akt-schuzka)",   label: "Schůzka" },
  contract: { icon: FileCheck,    color: "var(--positive)",      label: "Smlouva" },
  task:     { icon: ListTodo,     color: "var(--akt-ukol)",      label: "Úkol" },
  other:    { icon: HistoryIcon,  color: "var(--muted)",         label: "Aktivita" },
};

function classifyActivityType(item) {
  const raw = (item.type || item.kind || item.source || "").toLowerCase();
  if (item._source === "voice") return "voice";
  if (item._source === "contract") return "contract";
  if (item._source === "task") {
    if (raw === "meeting" || raw.includes("schůz")) return "meeting";
    if (raw === "call" || raw.includes("telef")) return "call";
    if (raw === "email" || raw.includes("mail")) return "email";
    return "task";
  }
  if (raw.includes("voice")) return "voice";
  if (raw.includes("meeting") || raw.includes("schůz")) return "meeting";
  if (raw.includes("call") || raw.includes("phone") || raw.includes("telef")) return "call";
  if (raw.includes("mail")) return "email";
  if (raw.includes("contract") || raw.includes("smlouv")) return "contract";
  if (raw.includes("task") || raw.includes("uk") || raw.includes("event")) return "task";
  return "other";
}

/* Částka smlouvy v MĚNĚ TENANTA a čísla podle jazyka (CRM-SK-06, 18. 10. 2026).
 * Smlouva měnu neukládá → je v měně poradce, stejně jako na kartě a v záložce Smlouvy.
 * Dřív natvrdo `toLocaleString("cs-CZ") + " Kč"` i u slovenského poradce. */
const fmtCastka = (n) => sJednotkou(Number(n).toLocaleString(localeUI()), menaTenanta().symbol);
/* Typ smlouvy čitelně (penzijko → Penzijní / Penzijné), ne syrový klíč. */
const typSmlouvy = (typ) => {
  const j = jazykUI() === "sk" ? "sk" : "cs";
  return (typ && (CT_LABEL[j][typ] || CT_LABEL.cs[typ])) || typ || TL("produkt");
};

const fmtDay = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(localeUI(), { day: "numeric", month: "short", year: "numeric" });
};

const fmtRelative = (iso, isFuture) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const diff = Math.round((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (isFuture) {
    if (diff === 0) return TL("dnes");
    if (diff === 1) return TL("zítra");
    if (diff < 0) return TL("před {n} dny").replace("{n}", Math.abs(diff));
    return TL("za {n} dní").replace("{n}", diff);
  } else {
    const abs = Math.abs(diff);
    if (abs === 0) return TL("dnes");
    if (abs === 1) return TL("včera");
    if (abs <= 7) return TL("před {n} dny").replace("{n}", abs);
    return TL("před {n} dny").replace("{n}", abs);
  }
};

export default function HistoryTimeline({ contactId, advisorName, advisorAvatar }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);
  // Které zdroje se nenačetly, když se ostatní načetly. Prázdné pole = vše OK.
  const [chybneZdroje, setChybneZdroje] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    let cancelled = false;
    // Reset při přepnutí klienta → žádná stale historie předchozího klienta.
    setItems(null);
    setError(false);
    setChybneZdroje([]);
    (async () => {
      try {
        // Označíme si, které zdroje selhaly. Když selžou VŠECHNY → to není „žádná
        // historie", ale chyba spojení → zobraz error+retry, ne falešné prázdno.
        const withFlag = (p) => p.then((r) => ({ ...r, _err: false })).catch(() => ({ data: [], _err: true }));
        /* ⚠️ CESTA K HLASOVKÁM. Do 20. 8. 2026 se volalo `/api/voice-notes`, jenže
         * backend má jen `/api/voice-notes/list` (routes/voice_notes.py). Živě to
         * vracelo 404, `withFlag` chybu spolklo a osa mlčky ukazovala historii bez
         * hlasovek — vypadalo to, že klient žádné nemá. Proto teď platí obojí:
         * správná cesta A hlášená chyba (viz `chybneZdroje` níž). Filtr na klienta
         * dělá server, ne my — `/list` vrací i `contact_id`, ale tahat 200 cizích
         * poznámek do prohlížeče kvůli jednomu klientovi nemá smysl. */
        const results = await Promise.all([
          withFlag(api.get(`/api/activities?contact_id=${contactId}`)),
          withFlag(api.get(`/api/voice-notes/list?contact_id=${encodeURIComponent(contactId)}`)),
          withFlag(api.get(`/api/contracts?contact_id=${contactId}`)),
          withFlag(api.get(`/api/crm/tasks?contact_id=${contactId}`)),
        ]);
        if (cancelled) return;
        const [a, v, c, t] = results;
        if (results.every((r) => r._err)) {
          setError(true);
          setItems([]);
          return;
        }
        /* ČÁSTEČNÉ SELHÁNÍ SE NESMÍ ZAMLČET. Když spadne jen jeden zdroj, zbytek
         * osy se vykreslí — ale poradce musí vědět, že to NENÍ celá historie.
         * Jinak si z neúplného obrazu udělá závěr („nevolal jsem mu") a řekne
         * ho klientovi nahlas. */
        setChybneZdroje(results.map((r, i) => (r._err ? ZDROJE[i] : null)).filter(Boolean));

        const activities = asArr(Array.isArray(a.data) ? a.data : a.data?.items).map((x) => ({
          ...x, _source: "activity",
          _date: x.created_at || x.updated_at || x.timestamp,
        }));
        /* Filtr je jen pojistka pro případ, že by server dotaz ignoroval —
         * `?contact_id=` už odfiltroval vše cizí. Poznámky bez `contact_id`
         * (nespárovaná hlasovka) do složky konkrétního klienta nepatří. */
        const voices = asArr(v.data).filter(
          (n) => (n.contact_id || "") === contactId
        ).map((n) => ({
          id: n.id,
          _source: "voice",
          title: n.title || TL("Hlasová poznámka"),
          subtitle: (n.analysis?.summary || n.transcript || "").slice(0, 200),
          _date: n.created_at,
          done: true,
        }));
        const contracts = asArr(c.data).map((ct) => ({
          id: ct.id,
          _source: "contract",
          title: `${TL("Smlouva")}: ${typSmlouvy(ct.type)}${ct.provider ? ` · ${ct.provider}` : ""}`,
          subtitle: [
            ct.monthly_amount ? `${fmtCastka(ct.monthly_amount)}/${TL("měs.")}` : null,
            ct.total_amount ? `${TL("celkem")} ${fmtCastka(ct.total_amount)}` : null,
            ct.fixation_end ? `${TL("fixace")} ${fmtDay(ct.fixation_end)}` : null,
            ct.anniversary_date ? `${TL("výročí")} ${fmtDay(ct.anniversary_date)}` : null,
          ].filter(Boolean).join(" · "),
          _date: ct.created_at || ct.start_date,
          done: true,
        }));
        const SOURCE_LABEL_HT = {
          meeting: "Schůzka", call: "Telefonát", email: "E-mail",
          letter: "Dopis", event: "Událost", task: "Úkol",
          voice_note: "Z hlasovky", from_manager: "Od manažera",
        };
        const tasks = asArr(t.data).map((ts) => {
          const srcLabel = TL(SOURCE_LABEL_HT[ts.source] || "Úkol");
          /* Obchod a místo mají lucide ikonu (dřív emoji sponky a špendlíku —
             30. 9. 2026, „ikony pryč"). Části se spojí tečkou níže. */
          const parts = [];
          if (ts.agenda) parts.push(ts.agenda);
          if (ts.deal_label) parts.push(<span key="obchod" className="inline-flex items-center gap-1" data-cast="obchod"><Paperclip size={11} strokeWidth={2} aria-hidden="true" />{ts.deal_label}</span>);
          if (ts.location) parts.push(<span key="misto" className="inline-flex items-center gap-1" data-cast="misto"><MapPin size={11} strokeWidth={2} aria-hidden="true" />{ts.location}</span>);
          if (ts.assignee_name && ts.assignee_id !== ts.assigner_id) parts.push(`→ ${ts.assignee_name}`);
          const spojene = parts.flatMap((p, i) => (i ? [" · ", p] : [p]));
          return {
            id: ts.id,
            _source: "task",
            source: ts.source,
            title: `${srcLabel}: ${ts.text || TL("(bez názvu)")}`,
            subtitle: (parts.length ? <>{spojene}</> : "") || (ts.deadline ? new Date(ts.deadline).toLocaleString(localeUI(), { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" }) : ""),
            _date: ts.deadline || ts.created_at,
            done: !!ts.done,
          };
        });

        const all = [...activities, ...voices, ...contracts, ...tasks].filter((x) => x._date);
        setItems(all);
      } catch {
        if (!cancelled) { setError(true); setItems([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [contactId, reloadKey]);

  const { future, past } = useMemo(() => {
    if (!items) return { future: null, past: null };
    const now = Date.now();
    const filtered = filter === "all" ? items : items.filter((it) => classifyActivityType(it) === filter);
    const fut = [], pst = [];
    filtered.forEach((it) => {
      const t = new Date(it._date).getTime();
      // Future = date in future AND not done (planned items)
      if (t > now && !it.done) fut.push(it);
      else pst.push(it);
    });
    fut.sort((a, b) => new Date(a._date) - new Date(b._date));    // ASC — nejbližší nahoře
    pst.sort((a, b) => new Date(b._date) - new Date(a._date));    // DESC — nejnovější nahoře
    return { future: fut, past: pst };
  }, [items, filter]);

  if (items === null && !error) {
    return <div className="py-12 text-center text-xs" style={{ color: "var(--ink-muted)" }}>{TL("Načítám historii…")}</div>;
  }

  if (error) {
    return <ErrorState onRetry={() => setReloadKey((k) => k + 1)} />;
  }

  const totalCount = (future?.length || 0) + (past?.length || 0);
  const hlasenka = hlasenkaZdroju(chybneZdroje);

  return (
    <div data-testid="history-timeline">
      {/* NEÚPLNÁ HISTORIE SE PŘIZNÁVÁ. Banner stojí NAD prázdným stavem schválně:
          když spadnou zrovna hlasovky a jiná historie není, ukázal by se jinak
          text „zatím žádná historie" — a to by byla nepravda, ne prázdno. */}
      {hlasenka && (
        <div
          data-testid="history-partial-error"
          className="flex items-start gap-2 mb-4 px-3 py-2 rounded-xl text-drobny"
          style={{
            background: "color-mix(in srgb, var(--cat-c, #BB5B43) 10%, #FFFFFF)",
            border: "1px solid color-mix(in srgb, var(--cat-c, #BB5B43) 32%, var(--border))",
            color: "var(--ink)",
          }}
        >
          <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" style={{ color: "var(--cat-c, #BB5B43)" }} />
          <span className="flex-1">{hlasenka}</span>
          <button
            onClick={() => setReloadKey((k) => k + 1)}
            data-testid="history-partial-retry"
            className="flex-shrink-0 font-semibold underline"
            style={{ color: "var(--negative-text)" }}
          >
            {TL("Zkusit znovu")}
          </button>
        </div>
      )}

      {/* Filter chips */}
      <div className="flex gap-1.5 flex-wrap mb-5">
        {FILTERS.map((f) => {
          const Icon = f.icon;
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`history-filter-${f.id}`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-drobny font-semibold transition-all"
              style={{
                background: active ? "var(--accent)" : "var(--surface)",
                color: active ? "var(--on-accent)" : "var(--ink)",
                border: `1px solid ${active ? "var(--accent)" : "var(--border)"}`,
                boxShadow: active ? "0 2px 8px rgba(194,89,52,0.20)" : "var(--elite-shadow)",
              }}
            >
              <Icon size={11} /> {TL(f.label)}
            </button>
          );
        })}
      </div>

      {totalCount === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* ─── Co nás čeká ─────────────────────────────────────── */}
          {future && future.length > 0 && (
            <Section
              title={TL("Co nás čeká")}
              count={future.length}
              icon={CalendarClock}
              accent="var(--accent)"
              accentTint="var(--accent-soft)"
              data-testid="history-section-future"
            >
              <Timeline items={future} advisorName={advisorName} advisorAvatar={advisorAvatar} isFuture />
            </Section>
          )}

          {/* ─── Co máme za sebou ────────────────────────────────── */}
          {past && past.length > 0 && (
            <Section
              title={TL("Co máme za sebou")}
              count={past.length}
              icon={CheckCircle2}
              accent="var(--positive-text)"
              accentTint="var(--positive-soft)"
              data-testid="history-section-past"
            >
              <Timeline items={past} advisorName={advisorName} advisorAvatar={advisorAvatar} isFuture={false} />
            </Section>
          )}
        </>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div
      className="rounded-2xl p-10 text-center"
      data-testid="history-empty"
      style={{ background: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--elite-shadow)" }}
    >
      <HistoryIcon size={28} className="mx-auto mb-2" style={{ color: "var(--ink-muted)" }} />
      <div className="text-sm font-medium" style={{ color: "var(--ink)" }}>{TL("Žádná historie")}</div>
      <div className="text-drobny mt-1" style={{ color: "var(--ink-muted)" }}>
        {TL("Nahrajte hlasovku nebo přidejte úkol — objeví se zde.")}
      </div>
    </div>
  );
}

function ErrorState({ onRetry }) {
  return (
    <div
      className="rounded-2xl p-10 text-center"
      data-testid="history-error"
      style={{ background: "var(--surface)", border: "1px solid color-mix(in srgb, var(--cat-c, #BB5B43) 30%, var(--border))", boxShadow: "var(--elite-shadow)" }}
    >
      <AlertTriangle size={28} className="mx-auto mb-2" style={{ color: "var(--cat-c, #BB5B43)" }} />
      <div className="text-sm font-medium" style={{ color: "var(--ink)" }}>{TL("Historii se nepodařilo načíst")}</div>
      <div className="text-drobny mt-1 mb-3" style={{ color: "var(--ink-muted)" }}>
        {TL("Nejde o prázdnou historii — spojení se serverem selhalo.")}
      </div>
      <button
        onClick={onRetry}
        data-testid="history-retry"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-drobny font-semibold"
        style={{ background: "var(--accent)", color: "var(--on-accent)" }}
      >
        {TL("Zkusit znovu")}
      </button>
    </div>
  );
}

function Section({ title, count, icon: Icon, accent, accentTint, children, ...rest }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mb-8"
      {...rest}
    >
      {/* Section header — sticky-feel pill */}
      <div className="flex items-center gap-2.5 mb-4">
        <div
          className="flex items-center justify-center w-8 h-8 rounded-full"
          style={{ background: accentTint, color: accent }}
        >
          <Icon size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[15px] font-bold tracking-tight" style={{ color: "var(--ink)", letterSpacing: "-0.01em" }}>
            {title}
          </h3>
        </div>
        <span
          className="text-popisek font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full"
          style={{ background: accentTint, color: accent, letterSpacing: "0.06em" }}
        >
          {count}
        </span>
      </div>
      {children}
    </motion.section>
  );
}

function Timeline({ items, advisorName, advisorAvatar, isFuture }) {
  return (
    <div className="relative pl-8">
      {/* Vertical spine */}
      <div
        className="absolute top-2 bottom-2 left-[14px] w-px"
        style={{
          background: isFuture
            ? "linear-gradient(to bottom, transparent, rgba(194,89,52,0.35) 20%, rgba(194,89,52,0.35) 80%, transparent)"
            : "linear-gradient(to bottom, transparent, rgba(34,134,90,0.30) 20%, rgba(34,134,90,0.30) 80%, transparent)",
        }}
      />

      <div className="space-y-3">
        {items.map((it, i) => {
          const type = classifyActivityType(it);
          const meta = TYPE_META[type] || TYPE_META.other;
          const Icon = meta.icon;
          const rel = fmtRelative(it._date, isFuture);
          return (
            <motion.div
              key={it.id || i}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: Math.min(i * 0.05, 0.6), duration: 0.35 }}
              className="relative"
              data-testid={`history-item-${i}`}
            >
              {/* Dot on spine */}
              <div
                className="absolute -left-[20px] top-3 w-5 h-5 rounded-full flex items-center justify-center"
                style={{
                  background: meta.color,
                  boxShadow: `0 0 0 4px var(--bg), 0 2px 8px color-mix(in srgb, ${meta.color} 33%, transparent)`,
                }}
              >
                <Icon size={10} color="var(--surface)" strokeWidth={2.6} />
              </div>

              <div
                className="rounded-xl p-3.5 transition-all hover:translate-y-[-1px]"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  boxShadow: "var(--elite-shadow)",
                }}
              >
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span
                        className="text-popisek uppercase tracking-wide px-1.5 py-0.5 rounded font-bold"
                        style={{
                          background: `color-mix(in srgb, ${meta.color} 10%, var(--surface))`,
                          color: `color-mix(in srgb, ${meta.color} 75%, var(--ink))`,
                          letterSpacing: "0.05em",
                        }}
                      >
                        {TL(meta.label)}
                      </span>
                      {it.done && !isFuture && (
                        <span className="text-drobny flex items-center gap-0.5" style={{ color: "var(--positive-text)" }}>
                          <CheckCircle2 size={10} /> {TL("Realizováno")}
                        </span>
                      )}
                      {rel && (
                        <span
                          className="text-drobny font-semibold ml-auto px-1.5 py-0.5 rounded mono"
                          style={{
                            color: isFuture ? "var(--accent-ink)" : "var(--ink-muted)",
                            background: isFuture ? "var(--accent-soft)" : "transparent",
                          }}
                        >
                          {rel}
                        </span>
                      )}
                    </div>

                    <div
                      className="text-sm font-medium leading-tight"
                      style={{ color: "var(--ink)" }}
                    >
                      {it.title || it.text || TL(meta.label)}
                    </div>

                    {(it.subtitle || it.summary) && (
                      <div
                        className="text-[12px] mt-1 leading-relaxed line-clamp-2"
                        style={{ color: "var(--ink-muted)" }}
                      >
                        {it.subtitle || it.summary}
                      </div>
                    )}

                    <div className="flex items-center gap-2 mt-2 text-drobny" style={{ color: "var(--ink-faint)" }}>
                      {advisorAvatar ? (
                        <img src={advisorAvatar} alt={advisorName} className="w-5 h-5 rounded-full" />
                      ) : (
                        <div
                          className="w-5 h-5 rounded-full flex items-center justify-center text-drobny font-bold"
                          style={{
                            background: "var(--accent-tint)",
                            color: "var(--accent)",
                            border: "1px solid var(--accent)",
                          }}
                        >
                          {(advisorName || "?").slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <span>{advisorName || TL("Poradce")}</span>
                      <span>·</span>
                      <Clock size={9} />
                      <span>{fmtDay(it._date)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
